import get from 'lodash/get';
import some from 'lodash/some';
import find from 'lodash/find';
import {
  EMPLOYER_ROLES, EMPLOYER_ROLE_ACCESS_LEVELS,
} from '../constants';

const fullName = (grant) => {
  if (grant) {
    return `${get(grant, 'firstName') || ''} ${get(grant, 'lastName') || ''}`.trim();
  }
  return null;
};

const isValidEmail = (email = '') => {
  // eslint-disable-next-line max-len
  const regex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/i;
  return regex.test(email);
};

const doesRoleExist = (grants, expectedRole) => some(grants, ['role', expectedRole]);

const hasJobcaseEmployerAdminRole = (grants) => {
  const grantsArray = Object.values(grants);
  let accessLevel = null;
  if (doesRoleExist(grantsArray, 'JobcaseEmployerAdmin')) {
    const { level } = grantsArray.find(grant => grant.role === 'JobcaseEmployerAdmin');
    accessLevel = level;
  }
  return accessLevel;
};

const hasWriteAccessForRole = (grants, role) => {
  let accessLevel = null;
  if (doesRoleExist(grants, role)) {
    const { level } = find(grants, grant => grant.role === role);
    accessLevel = level;
  }
  return accessLevel && accessLevel !== EMPLOYER_ROLE_ACCESS_LEVELS.READ && accessLevel !== EMPLOYER_ROLE_ACCESS_LEVELS.ADMIN_READ;
};

const hasAdminWriteAccessForRole = (grants, role) => {
  let accessLevel = null;
  if (doesRoleExist(grants, role)) {
    const { level } = find(grants, grant => grant.role === role);
    accessLevel = level;
  }
  return accessLevel && accessLevel === EMPLOYER_ROLE_ACCESS_LEVELS.ADMIN_WRITE;
};

const hasAdminReadAccessForRole = (grants, role) => {
  let accessLevel = null;
  if (doesRoleExist(grants, role)) {
    const { level } = find(grants, grant => grant.role === role);
    accessLevel = level;
  }
  return accessLevel && accessLevel === EMPLOYER_ROLE_ACCESS_LEVELS.ADMIN_READ;
};

const hasReadAccessForRole = (grants, role) => {
  let accessLevel = null;
  if (doesRoleExist(grants, role)) {
    const { level } = find(grants, grant => grant.role === role);
    accessLevel = level;
  }

  return accessLevel && (accessLevel === EMPLOYER_ROLE_ACCESS_LEVELS.ADMIN_READ
    || EMPLOYER_ROLE_ACCESS_LEVELS.ADMIN_WRITE
    || EMPLOYER_ROLE_ACCESS_LEVELS.WRITE
    || EMPLOYER_ROLE_ACCESS_LEVELS.READ);
};

const hasAdminAccessForRole = (grants, role) => hasAdminWriteAccessForRole(grants, role) || hasAdminReadAccessForRole(grants, role);

const hasWriteAccessForHiringEvents = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasWriteAccessForRole(grants, EMPLOYER_ROLES.HIRING_EVENT_MANAGER);
};

const hasAdminWriteAccessForHiringEvents = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.HIRING_EVENT_MANAGER);
};

const hasAdminReadAccessForHiringEvents = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminReadAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminReadAccessForRole(grants, EMPLOYER_ROLES.HIRING_EVENT_MANAGER);
};

const hasWriteAccessForUserRolesPermissions = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasWriteAccessForRole(grants, EMPLOYER_ROLES.USER_ADMINISTRATOR);
};

const hasReadAccessForUserRolesPermissions = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.USER_ADMINISTRATOR)) {
    return hasReadAccessForRole(grants, EMPLOYER_ROLES.USER_ADMINISTRATOR);
  }
  return hasReadAccessForRole(grants, EMPLOYER_ROLES.USER_ADMINISTRATOR);
};

const hasAdminAccessForUserRolesPermissions = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminAccessForRole(grants, EMPLOYER_ROLES.USER_ADMINISTRATOR);
};

const hasWriteAccessForHubs = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasWriteAccessForRole(grants, EMPLOYER_ROLES.COMPANY_HUB_MANAGER);
};

const hasAdminWriteAccessForHubs = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.COMPANY_HUB_MANAGER);
};

const hasAdminReadAccessForHubs = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminReadAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminReadAccessForRole(grants, EMPLOYER_ROLES.COMPANY_HUB_MANAGER);
};

const hasWriteAccessForJobs = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasWriteAccessForRole(grants, EMPLOYER_ROLES.JOBS_MANAGER);
};

const hasAdminWriteAccessForJobs = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.JOBS_MANAGER);
};

const hasWriteAccessForJobCampaigns = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasWriteAccessForRole(grants, EMPLOYER_ROLES.JOB_CAMPAIGNS_MANAGER);
};

const hasAdminWriteAccessForJobCampaigns = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.JOB_CAMPAIGNS_MANAGER);
};

const hasWriteAccessForSettings = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasWriteAccessForRole(grants, EMPLOYER_ROLES.SETTINGS_MANAGER);
};

const hasAdminWriteAccessForSettings = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminWriteAccessForRole(grants, EMPLOYER_ROLES.SETTINGS_MANAGER);
};

const hasAdminReadAccessForSettings = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasAdminReadAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasAdminReadAccessForRole(grants, EMPLOYER_ROLES.SETTINGS_MANAGER);
};

const hasWriteAccessForBilling = (grants) => {
  if (doesRoleExist(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER)) {
    return hasWriteAccessForRole(grants, EMPLOYER_ROLES.EMPLOYER_MODULE_MANAGER);
  }
  return hasWriteAccessForRole(grants, EMPLOYER_ROLES.BILLING_MANAGER);
};

export {
  fullName,
  isValidEmail,
  doesRoleExist,
  hasWriteAccessForHiringEvents,
  hasAdminReadAccessForHiringEvents,
  hasAdminWriteAccessForHiringEvents,
  hasWriteAccessForUserRolesPermissions,
  hasAdminAccessForUserRolesPermissions,
  hasJobcaseEmployerAdminRole,
  hasWriteAccessForHubs,
  hasAdminWriteAccessForHubs,
  hasAdminReadAccessForHubs,
  hasWriteAccessForJobs,
  hasAdminWriteAccessForJobs,
  hasWriteAccessForSettings,
  hasAdminWriteAccessForSettings,
  hasAdminReadAccessForSettings,
  hasWriteAccessForBilling,
  hasWriteAccessForJobCampaigns,
  hasAdminWriteAccessForJobCampaigns,
  hasReadAccessForUserRolesPermissions,
};

export default {
  fullName,
  isValidEmail,
  doesRoleExist,
  hasWriteAccessForHiringEvents,
  hasAdminReadAccessForHiringEvents,
  hasAdminWriteAccessForHiringEvents,
  hasWriteAccessForUserRolesPermissions,
  hasAdminAccessForUserRolesPermissions,
  hasJobcaseEmployerAdminRole,
  hasWriteAccessForHubs,
  hasAdminWriteAccessForHubs,
  hasAdminReadAccessForHubs,
  hasWriteAccessForJobs,
  hasAdminWriteAccessForJobs,
  hasWriteAccessForSettings,
  hasAdminWriteAccessForSettings,
  hasAdminReadAccessForSettings,
  hasWriteAccessForBilling,
  hasWriteAccessForJobCampaigns,
  hasAdminWriteAccessForJobCampaigns,
  hasReadAccessForUserRolesPermissions,
};
